import { LeadPipelineStatus } from "@inrange/building-manager-api-client";

// How a lead's contacts-pipeline status RENDERS. The stage machine and the wording both
// live on the backend (lead_pipeline_status.py) so the Status column and the right-hand
// panel cannot drift; this file only decides colour and whether a spinner shows, which
// are presentation-only and would be meaningless server-side.
//
// Deliberately NOT a second copy of the stage rules: every entry on the lead pipeline's
// "surfaces that must agree" list is one rule that got copied and then diverged.

export type PipelineTone =
  // Work is in flight - shows a spinner.
  | "working"
  // Finished with emails to show for it.
  | "done"
  // Finished, but nothing was produced. A settled fact, not a failure.
  | "empty"
  // Stopped somewhere it could be picked up: a spent quota, a run that never started,
  // a capped tail. The one tone that wants an operator.
  | "attention"
  // Nothing to say (no company chosen yet).
  | "idle";

const TONE_BY_STAGE: Record<string, PipelineTone> = {
  drafted: "done",
  no_contacts: "empty",
  no_top_targets: "empty",
  no_email: "empty",
  quota_spent: "attention",
  not_run: "attention",
  truncated: "attention",
  no_company: "idle",
};

export const pipelineTone = (status: LeadPipelineStatus): PipelineTone => {
  if (status.running) return "working";
  const tone = TONE_BY_STAGE[status.stage];
  if (tone) return tone;
  // A stage key with no live operation: the run stopped part-way. The label already
  // reads as unfinished work ("1 of 3 emails drafted"), so the tone matches it.
  return "attention";
};

export const TONE_COLOUR: Record<PipelineTone, string> = {
  working: "#0a58ca",
  done: "#0f5132",
  empty: "#6c757d",
  attention: "#b26a00",
  idle: "#9aa2ad",
};

// The one line the column shows. The counter is appended only while work is in flight:
// once it stops, the BE's label already carries the numbers in words ("1 of 3 emails
// drafted"), and appending "(1/3)" to that would say the same thing twice.
export const pipelineStatusText = (status: LeadPipelineStatus): string => {
  if (!status.label) return "";
  if (status.running && status.total != null && status.total > 0) {
    return `${status.label} (${status.done ?? 0}/${status.total})`;
  }
  return status.label;
};

// Stages that say nothing about CONTACT DETAILS. The status is lead-level - one line
// reduced from both roles - so a per-role cell must not repeat it wholesale: while
// discovery is running, "Finding contacts" in a contact-details box claims we are
// looking for details we have not started on, and in a role with no company chosen it
// claims work that is not happening at all. Everything else is kept, including the
// terminals: "Apollo quota spent", "3 contacts, no email found" and "Contact details
// not pulled (2 to pull)" are all exactly why the box is empty.
const NOT_ABOUT_DETAILS = new Set(["finding", "ranking", "no_company"]);

// The status a per-role contact-details box should show, or null for "say nothing
// beyond your own empty text". One place decides this, because the alternative is the
// rule inlined at each of the three cells - which is how the lead-pipeline surfaces
// have drifted before.
export const contactDetailsStatus = (
  status: LeadPipelineStatus | null | undefined,
  hasCompany: boolean
): LeadPipelineStatus | null => {
  if (!status || !hasCompany) return null;
  return NOT_ABOUT_DETAILS.has(status.stage) ? null : status;
};
